import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Container, CircularProgress, Grid, Alert, Avatar, Stack } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { debounce } from 'lodash';
import WbTextField from '../../../components/common/WbTextField';
import WbLoader from '../../../components/common/WbLoader';
import KioskLayout from '../../../components/kiosk/KioskLayout';
import { useKiosk } from '../../../contexts/KioskContext';
import kioskApi from '../../../services/api/kiosk/kioskApi';
import paths from '../../../routes/paths';
import '../KioskStyles.scss';

export default function StudentSearch() {
  const { schoolSlug } = useParams();
  const { school, loading: schoolLoading } = useKiosk();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const performSearch = useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 3) {
      setResults([]);
      return;
    }

    setSearching(true);
    try {
      const data = await kioskApi.searchStudents(schoolSlug, searchQuery);
      setResults(data || []);
    } catch (err) {
      console.error('Search error:', err);
      setResults([]);
    } finally {
      setSearching(false);
    }
  }, [schoolSlug]);

  const debouncedSearch = useCallback(
    debounce((searchQuery) => {
      performSearch(searchQuery);
    }, 300),
    [performSearch]
  );

  useEffect(() => {
    if (query.length >= 3) {
      debouncedSearch(query);
    } else {
      setResults([]);
    }
  }, [query, debouncedSearch]);

  const handleSelectStudent = (student) => {
    navigate(paths.kioskStudentVerify.replace(':schoolSlug', schoolSlug), {
      state: { student }
    });
  };

  if (schoolLoading) {
    return (
      <KioskLayout>
        <WbLoader />
      </KioskLayout>
    );
  }

  return (
    <KioskLayout>
      <Container maxWidth="md">
        <div className="kiosk-title">
          Search for Your Name
        </div>
        <div className="kiosk-subtitle">
          Type at least 3 letters to search
        </div>

        <Box mt={2}>
          <WbTextField
            label="Your Name"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            //className="kiosk-text-field-large"
            autoFocus
          />
          {searching && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}
        </Box>

        {results.length > 0 && (
          <Grid container spacing={2} mt={2}>            
            {results.map((result) => (
              <Grid item xs={12} sm={6} md={4}>
                <div
                  key={result.id}
                  className="kiosk-result-item"
                  onClick={() => handleSelectStudent(result)}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Avatar>{result.fullName ? result.fullName.charAt(0) : (result.firstName ? result.firstName.charAt(0) : '')}</Avatar>
                    <div>
                      <Typography className="result-name">{result.fullName || `${result.firstName} ${result.lastName}`}</Typography>
                      <Typography className="result-class">Reg No: {result.registrationClass}</Typography>
                    </div>
                  </Stack>
                </div>
              </Grid>
            ))}            
          </Grid>
        )}

        {query.length > 0 && query.length < 3 && (
          <Alert severity="info" sx={{ mt: 2 }}>Please type at least 3 letters</Alert>          
        )}

        {query.length >= 3 && results.length === 0 && !searching && (
          <Alert severity="info" sx={{ mt: 2 }}>No results found</Alert>
        )}
      </Container>
    </KioskLayout>
  );
}
